import { auth } from "./firebase";

/**
 * Join VITE_API_BASE with a path. If the base already ends with `/api` and the
 * path starts with `/api/`, the duplicate segment is removed so we never
 * request `/api/api/...` (which returns 404 from FastAPI).
 */
function resolveApiUrl(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  let base = (import.meta.env.VITE_API_BASE ?? "").trim();
  if (!base) return p;
  base = base.replace(/\/+$/, "");
  if (base.endsWith("/api") && p.startsWith("/api/")) {
    return `${base}${p.slice(4)}`;
  }
  return `${base}${p}`;
}

export class ApiError extends Error {
  status: number;
  body: string;
  constructor(status: number, body: string, message?: string) {
    super(message ?? `HTTP ${status}: ${body}`);
    this.status = status;
    this.body = body;
  }
}

async function buildHeaders(extra?: HeadersInit, json = true): Promise<Headers> {
  const headers = new Headers(extra);
  if (json && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      headers.set("Authorization", `Bearer ${token}`);
    } catch {
      // no-op: caller will hit 401 and redirect
    }
  }
  return headers;
}

export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = await buildHeaders(init.headers, init.body != null);
  const resp = await fetch(resolveApiUrl(path), { ...init, headers });
  return resp;
}

export async function apiJson<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const resp = await apiFetch(path, init);
  if (!resp.ok) {
    const text = await resp.text();
    throw new ApiError(resp.status, text, `HTTP ${resp.status}: ${text}`);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export async function apiPost<T = unknown>(
  path: string,
  body?: unknown,
): Promise<T> {
  return apiJson<T>(path, {
    method: "POST",
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

export async function apiGet<T = unknown>(path: string): Promise<T> {
  return apiJson<T>(path, { method: "GET" });
}

export async function apiDelete<T = unknown>(path: string): Promise<T> {
  return apiJson<T>(path, { method: "DELETE" });
}

export async function apiPatch<T = unknown>(
  path: string,
  body?: unknown,
): Promise<T> {
  return apiJson<T>(path, {
    method: "PATCH",
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

export async function apiPut<T = unknown>(
  path: string,
  body?: unknown,
): Promise<T> {
  return apiJson<T>(path, {
    method: "PUT",
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

/**
 * Stream NDJSON from a POST endpoint. Invokes onEvent for every parsed line.
 * Returns the AbortController so the caller can cancel the stream.
 */
export interface NdjsonStreamOpts<T> {
  path: string;
  body: unknown;
  onEvent: (ev: T) => void;
  onError?: (message: string) => void;
}

export function streamNdjson<T = unknown>(
  opts: NdjsonStreamOpts<T>,
): { promise: Promise<void>; abort: () => void } {
  const controller = new AbortController();

  const promise = (async () => {
    const headers = await buildHeaders();
    const resp = await fetch(resolveApiUrl(opts.path), {
      method: "POST",
      headers,
      body: JSON.stringify(opts.body),
      signal: controller.signal,
    });
    if (!resp.ok) {
      const detail = await resp.text();
      throw new ApiError(resp.status, detail);
    }
    const reader = resp.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf("\n")) >= 0) {
        const line = buffer.slice(0, nl).trim();
        buffer = buffer.slice(nl + 1);
        if (!line) continue;
        try {
          opts.onEvent(JSON.parse(line) as T);
        } catch (e) {
          opts.onError?.(`bad event: ${line}`);
        }
      }
    }
  })().catch((err) => {
    if ((err as Error).name !== "AbortError") {
      opts.onError?.((err as Error).message);
      throw err;
    }
  });

  return { promise, abort: () => controller.abort() };
}
